import {
  Button,
  Dropdown,
  detectCtrlOrCommandClick,
  Icon,
  log,
  type Option,
} from "@hoylu/client-common";
import DefaultDropdownStyles from "@hoylu/client-common/dist/assets/css.modules/default.dropdown.module.css";
import Scrollbar from "@hoylu/client-common/dist/assets/css.modules/scrollbar.module.css";
// check this import
import {
  NexusInsightsLandingPage,
  NexusInsightsProjectContextProvider,
} from "@hoylu/nexus-insights-panel";
import {
  CalendarService,
  HistoryService,
  ImportService,
  ReportService,
  TaskRelationsService,
  TaskService,
} from "@hoylu/nexus-service";
import { UserSettingsContext } from "@hoylu/user-settings";
import Color from "color";
import React, { type CSSProperties, useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DashboardOption } from "@/enums/DashboardOption.enum";
import { fetchMasterData, reset } from "../state/analytics/analytics.actions";
import {
  isMasterDataRequestFailed,
  selectHasEmptyScope,
} from "../state/analytics/analytics.selector";
import { config, featureFlags } from "../state/config/config.selector";
import { user } from "../state/user/user.selector";
import {
  getAllProjectCollaborators,
  isAdminOfSelectedProject,
  selectDashboardSelectedProject,
} from "../state/workspaces/projects.selector";
import type { Project } from "../state/workspaces/types";
import { activateDashboardOption } from "../state/workspaces/workspaces.actions";
import { useI18n } from "../utils/hooks/use.i18n";
import { useSingleSelectDropdownTranslation } from "../utils/hooks/useSingleSelectDropdownTranslation";
import { managementPortalProjectUrl } from "../utils/management.portal/management.portal.url.selectors";
import { openManagementPortal } from "../utils/management.portal/openManagementPortal";
import { projectDeepLink } from "../utils/url.selectors";
import { Analytics } from "./analytics/Analytics";
import { FilterProvider } from "./analytics/FilterProvider";
import type { ReportType } from "./analytics/ReportFactory";
import { CollaboratorMarble } from "./CollaboratorMarble";
import SectionLayout from "./layouts/SectionLayout";
import { CopyIcon } from "./modals/sharing/icons/CopyIcon";
import Styles from "./ProjectSection.module.css";

const availableReports: Option<ReportType>[] = [
  { label: "Task by Status", value: "status" },
  { label: "PPC", value: "ppc" },
  { label: "Variance Reasons", value: "reason" },
];

const editIconStyle: CSSProperties = {
  color: "var(--theme-text)",
  fontSize: "var(--global--size-x-large)",
};

const ProjectSection: React.FC = () => {
  const dispatch = useDispatch();
  const t = useI18n("SECTIONS.PROJECT.");
  const dropdownTranslation = useSingleSelectDropdownTranslation();

  const deepLink = useSelector(projectDeepLink);
  const manageProjectUrl = useSelector(managementPortalProjectUrl);
  const collaborators = useSelector(getAllProjectCollaborators);
  const isAdmin = useSelector(isAdminOfSelectedProject);
  const project = useSelector(selectDashboardSelectedProject);
  const flags = useSelector(featureFlags);
  const hasEmptyScope = useSelector(selectHasEmptyScope);
  const isMissingMasterData = useSelector(isMasterDataRequestFailed);

  const [report, setReport] = useState(availableReports[0]);

  const showCollaborators = collaborators.length > 0;
  const showProjectSettings = isAdmin && !!manageProjectUrl;

  const enableNexusInsights = flags.enableNexusInsightsPanel;
  const showNexusReporting =
    !enableNexusInsights &&
    flags.enableNexusReporting &&
    !hasEmptyScope &&
    !isMissingMasterData;
  const showDeleteScope =
    flags.showNexusDeleteScope && (showNexusReporting || enableNexusInsights);

  const backgroundColor = Color(project?.color || "dimgrey");

  return (
    <SectionLayout>
      <div
        aria-label={t("COLLABORATORS")}
        className={`${Styles.header} ${Scrollbar.normal} ${Scrollbar.horizontal}`}
        tabIndex={0}
      >
        <div className={Styles.titleSection}>
          <span>{t("DASHBOARD")}</span>
          {showNexusReporting && (
            <Dropdown
              allowReset={false}
              classes={[DefaultDropdownStyles.dropdown, Styles.dropdown]}
              localizationDict={dropdownTranslation}
              onChange={(val) => setReport(val[0])}
              options={availableReports}
              title={t("REPORT_TYPE")}
              useMultiSelect={false}
              values={[report]}
            />
          )}
          {!!project?.id && enableNexusInsights && (
            <Button
              additionalStyles={{
                "--nexus-insights-button-bg": backgroundColor.hex(),
              }}
              classNames={[
                Styles.nexusInsightsButton,
                backgroundColor.isDark() ? "theme-dark" : "theme-light", // toggle text color based on background color
              ]}
              onClick={(e) => {
                // If user left click with ctrl/meta
                if (
                  flags.enableInsightsHomeApp &&
                  detectCtrlOrCommandClick(e)
                ) {
                  window.open(
                    `${window.location.origin}/project/${project?.id}/insights`,
                    "_blank",
                  );
                } else {
                  dispatch(
                    activateDashboardOption({
                      optionType: DashboardOption.NEXUS_INSIGHTS_PANEL,
                    }),
                  );
                }
              }}
              toolTip={t("PROJECT_INSIGHTS")}
            >
              <span className="hoylu-ui-icons-chart" />
              {t("PROJECT_INSIGHTS")}
            </Button>
          )}
        </div>
        <div className={Styles.collaboratorsSection}>
          {showCollaborators && (
            <div
              aria-label={t("COLLABORATORS")}
              className={`${Styles.collaborators} ${Scrollbar.normal} ${Scrollbar.horizontal}`}
              tabIndex={0}
            >
              {collaborators.map((collaborator) => (
                <CollaboratorMarble
                  collaborator={collaborator}
                  key={collaborator.userId}
                />
              ))}
            </div>
          )}
          <div className={Styles.copyIcon} tabIndex={0} title={t("COPY_LINK")}>
            <CopyIcon
              icon="hoylu-ui-icons-link"
              style={editIconStyle}
              text={deepLink}
            />
          </div>
          {showProjectSettings && (
            <>
              <div tabIndex={0} title={t("PROJECT_SETTINGS")}>
                <Icon
                  icon="hoylu-ui-icons-setting"
                  onClick={() => openManagementPortal(manageProjectUrl)}
                  style={editIconStyle}
                />
              </div>
              {showDeleteScope && (
                <div tabIndex={0} title={t("DELETE_SCOPE")}>
                  <Icon
                    icon="hoylu-ui-icons-trash"
                    onClick={() =>
                      dispatch(
                        activateDashboardOption({
                          optionType: DashboardOption.DROP_SCOPE,
                        }),
                      )
                    }
                    style={editIconStyle}
                  />
                </div>
              )}
            </>
          )}
          {showNexusReporting && (
            <div tabIndex={0} title={t("RELOAD")}>
              <Icon
                icon="hoylu-ui-icons-refresh"
                onClick={() => {
                  dispatch(reset());
                  project?.id && dispatch(fetchMasterData.request(project.id));
                }}
                style={editIconStyle}
              />
            </div>
          )}
        </div>
      </div>
      {flags.enableNexusReporting && !enableNexusInsights && (
        <FilterProvider>
          {!hasEmptyScope && !isMissingMasterData && (
            <Analytics type={report.value} />
          )}
        </FilterProvider>
      )}
      {enableNexusInsights && project && (
        <NexusInsightsLandingPageSection project={project} />
      )}
    </SectionLayout>
  );
};

type NexusInsightsLandingPageSectionProps = {
  project: Project;
};

const NexusInsightsLandingPageSection = ({
  project,
}: NexusInsightsLandingPageSectionProps) => {
  "use memo";

  const configState = useSelector(config);
  const userState = useSelector(user);
  const settingsContext = useContext(UserSettingsContext);
  const localizationCode = settingsContext.userSettings?.currentLanguage;

  const nexusReportingService = new ReportService({
    scopeId: project.id,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusTaskService = new TaskService({
    scopeId: project.id,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusTaskRelationsService = new TaskRelationsService({
    scopeId: project.id,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusTaskHistoryService = new HistoryService({
    scopeId: project.id,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusCalendarService = new CalendarService({
    scopeId: project.id,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  const nexusImportService = new ImportService({
    scopeId: project.id,
    server: configState.serviceConfig.nexus,
    credentialProvider: () => userState,
    logger: log,
  });

  return (
    <div>
      <NexusInsightsProjectContextProvider
        color={project.color ?? "var(--global--light-primary--Color)"}
        icon={project.icon ?? "hoylu-ui-icons-chart"}
        name={project.name ?? "Nexus Insights"}
        scopeId={project.id}
      >
        <NexusInsightsLandingPage
          localizationCode={localizationCode}
          nexusCalendarService={nexusCalendarService}
          nexusImportService={nexusImportService}
          nexusReportingService={nexusReportingService}
          nexusTaskHistoryService={nexusTaskHistoryService}
          nexusTaskRelationsService={nexusTaskRelationsService}
          nexusTaskService={nexusTaskService}
        />
      </NexusInsightsProjectContextProvider>
    </div>
  );
};

export default React.memo(ProjectSection);
